import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { PROJECTS } from "../config";

function ProjectCard({ project, flip }) {
  return (
    <Reveal>
      <article
        data-testid={`project-card-${project.number}`}
        className={`grid md:grid-cols-12 gap-8 md:gap-12 items-center border-t border-line pt-10 md:pt-14 ${
          flip ? "" : ""
        }`}
      >
        <a
          href={project.demoUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`project-image-link-${project.number}`}
          className={`group relative block md:col-span-7 overflow-hidden border border-line ${
            flip ? "md:order-2" : ""
          }`}
          aria-label={`Open live demo of ${project.name} in a new tab`}
        >
          <div className="overflow-hidden">
            <img
              src={project.image}
              alt={`Homepage preview of the ${project.name} website`}
              loading="lazy"
              className="w-full aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.035]"
            />
          </div>
          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-500" />
          <span className="absolute bottom-5 right-5 inline-flex items-center gap-2 bg-ivory text-ink text-sm font-semibold px-4 py-2.5 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-400">
            View Project <ArrowUpRight size={15} />
          </span>
        </a>

        <div className={`md:col-span-5 ${flip ? "md:order-1" : ""}`}>
          <p className="font-heading text-5xl md:text-6xl text-ink/15 font-medium">{project.number}</p>
          <h3 className="mt-2 font-heading text-3xl md:text-4xl font-medium tracking-tight text-ink">
            {project.name}
          </h3>
          <p className="mt-1 text-xs uppercase tracking-[0.22em] font-bold text-accent">{project.category}</p>
          <p className="mt-4 text-base leading-relaxed text-stone max-w-sm">{project.description}</p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {project.services.map((s) => (
              <li
                key={s}
                className="text-xs font-semibold tracking-wide uppercase border border-ink/20 text-ink/70 px-3 py-1.5"
              >
                {s}
              </li>
            ))}
          </ul>
          <a
            href={project.demoUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`project-demo-link-${project.number}`}
            className="group mt-7 inline-flex items-center gap-2 bg-ink text-ivory font-semibold tracking-wide px-6 py-3.5 hover:bg-accent transition-colors duration-300"
          >
            View Live Demo
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <p className="mt-3 text-xs text-stone/70">Demo concept — opens in a new tab</p>
        </div>
      </article>
    </Reveal>
  );
}

export default function Work() {
  return (
    <section id="work" data-testid="work-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <Reveal>
        <div className="flex items-baseline gap-4">
          <span className="font-heading text-xl text-accent">01</span>
          <Eyebrow>Selected Work</Eyebrow>
        </div>
        <h2 className="mt-6 font-heading font-medium tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl">
          Three businesses.<br />Three completely different experiences.
        </h2>
        <p className="mt-6 text-base md:text-lg text-stone leading-relaxed max-w-xl">
          Every business has a different personality. These projects demonstrate
          how I adapt the design, structure and experience to the brand.
        </p>
      </Reveal>

      <div className="mt-16 md:mt-24 space-y-20 md:space-y-28">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.number} project={p} flip={i % 2 === 1} />
        ))}
      </div>
    </section>
  );
}
